/**
 * CallDetails.js
 * Talkroute
 *
 * Created by Vel Genov on 2/19/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
/* eslint react/prop-types: 0 */

import { isIOS } from "@constants/appConstants";
import { Grid } from "@material-ui/core";
import { push } from "connected-react-router";
import React from "react";
import { connect } from "react-redux";
import RelatedCallsDisplay from "../components/CallHistory/RelatedCallsDisplay";
import uiString from "../constants/uiString";

interface ICallDetailsProps {
  title?: string;
  relatedCalls: any[];
  children?: any;
  parent: any;
  buttons?: any;
  readIndicator?: any;
  lastMessage?: any;
  titleBlockStyle?: any;
  titleBlockHeaderStyle?: any;
  iconsBlockStyle?: any;
  toggleCallBack?: () => void;
  hideDetails?: (parent?: any) => any;
  push: (path: string) => any;
}

/**
 * A container React component. Implemented following the composition paradigm
 * https://reactjs.org/docs/composition-vs-inheritance.html
 *
 * Displays the common parts of call detais:
 * * Header
 * * Content section
 * * Function buttons
 * * Related calls
 * The content section is filled out by whoever uses the container
 *
 * @param title The section title
 * @param relatedCalls The related calls to display
 * @param hideDetails The function to call when hiding this component
 * @param parent The parent to go back to when hidden
 * @param children JSX to display in the content section
 * @param buttons JSX of the buttons to display
 */
class CallDetails extends React.Component<ICallDetailsProps, any> {
  static defaultProps = {
    relatedCalls: [],
  };

  closeCallHistoryDetails = (props) => {
    const { parent, hideDetails: hideDetailsLocal, push } = props;
    push(parent);
    hideDetailsLocal(parent);
  };

  /**
   * Draw the main content
   * @param children JSX to display in the content section
   * @param related a list of related calls to display
   */
  showContent = (children, relatedCalls, lastMessage) => {
    const callDetailStyle = {
      borderTop: "1px solid var(--color-dark-grey)",
      borderBottom: "1px solid var(--color-dark-grey)",
      margin: "1rem -1rem",
      padding: "26px",
      marginTop: "16px",
    };
    const relatedCallStyle = {
      margin: "-1rem -1rem",
      padding: "26px",
      backgroundColor: "var(--color-background-grey)",
      flex: "2",
    };

    if (!children) {
      return (
        <div style={{ ...callDetailStyle, padding: 13, borderBottom: "none" }}>
          <h5 style={{ textAlign: "center" }} className="none-select-text">
            {uiString.NO_DETAILS_AVAILABLE}
          </h5>
        </div>
      );
    }

    if (!lastMessage) {
      return (
        <div style={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <div style={callDetailStyle}>{children}</div>
          <div style={relatedCallStyle}>
            {/* Related calls section */}
            <div style={{ paddingBottom: "28px" }}>
              <h5 className="text-large none-select-text">
                {uiString.RELATED_CALLS}
              </h5>
            </div>
            <RelatedCallsDisplay relatedCalls={relatedCalls} />
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", flex: "1" }}>
        <div
          style={{
            ...callDetailStyle,
            flex: "1",
            borderLeft: "1px solid var(--color-dark-grey)",
          }}
        >
          {children}
        </div>
        <div
          style={{
            ...relatedCallStyle,
            borderLeft: "1px solid var(--color-dark-grey)",
          }}
        >
          {/* Related calls section */}
          <div>
            <h5 className="text-large none-select-text">
              {uiString.conversationDetails.LAST_MESSAGE_DETAILS}
            </h5>
          </div>
          {lastMessage}
        </div>
      </div>
    );
  };

  render() {
    const {
      title,
      relatedCalls,
      hideDetails,
      toggleCallBack,
      children,
      buttons,
      readIndicator,
      lastMessage,
      titleBlockStyle,
      titleBlockHeaderStyle,
      iconsBlockStyle,
    } = this.props;

    let WebkitAppRegion = "inherit";
    if (isIOS) WebkitAppRegion = "drag";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "1em",
          height: "100%",
          width: "100%",
          paddingTop: 11,
        }}
      >
        {/* Header */}
        <Grid
          container
          style={
            {
              minHeight: "32px",
              height: "32px",
              WebkitAppRegion,
              zIndex: 5,
            } as any
          }
        >
          {title && (
            <Grid item style={titleBlockStyle} xs={6}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <h3
                  style={titleBlockHeaderStyle}
                  onClick={toggleCallBack}
                  className="text-normal none-select-text"
                >
                  {title}
                </h3>
                {readIndicator}
              </div>
            </Grid>
          )}
          {/* Header buttons */}
          <Grid
            item
            style={iconsBlockStyle}
            xs={6}
          >
            {buttons}
          </Grid>
        </Grid>
        {/* Content section */}
        {this.showContent(children, relatedCalls, lastMessage)}
      </div>
    );
  }
}

export default connect(() => ({}), { push })(CallDetails);
